import { CalendarPickerView } from '@mui/lab'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { Dayjs } from 'dayjs'
import { useState } from 'react'
import { Controller, UseControllerProps, useFormContext } from 'react-hook-form'
import { match, P } from 'ts-pattern'
import { DateView } from '@mui/x-date-pickers'
import 'dayjs/locale/pt-br'

type FormPickerProps = Omit<UseControllerProps, 'control'> & {
    disabled?: boolean
    label?: string
    name: string
    required?: boolean
    size?: 'small' | 'medium' | undefined
    static?: boolean
    type?: 'date' | 'month' | 'day'
    variant?: 'filled' | 'outlined' | 'standard'
    readonly?: boolean
    defaultValue?: string
    unHelperText?: boolean
    helperText?: string
    hidden?: boolean
}

export function FormDatePicker({
    label = 'Competencia',
    name,
    required,
    rules,
    size = 'medium',
    type,
    variant = 'filled',
    defaultValue,
    disabled: disabledPicker,
    unHelperText,
    helperText,
    readonly,
    hidden,
}: FormPickerProps) {
    const { control, watch, setValue } = useFormContext()
    const disabled = disabledPicker || (control as any)._options?.disabled

    const value = watch(name, defaultValue)

    const [date, setDate] = useState(() => (value ? dayjs(value) : null))

    const viewsPicker = match(type)
        .with('date', () => ['day', 'month', 'year'])
        .with('month', () => ['year', 'month'])
        .with('day', () => ['day'])
        .otherwise(() => ['year', 'month', 'day']) as CalendarPickerView[]

    const handleChange = (date: Dayjs | null) => {
        setDate(date)
        setValue(
            name,
            match([type, date])
                .with(['date', P.nonNullable.select()], value =>
                    value.format('YYYY-MM-DD'),
                )
                .with(['month', P.nonNullable.select()], value =>
                    value.format('YYYY-MM'),
                )
                .with(['day', P.nonNullable.select()], value =>
                    value.format('DD'),
                )
                .with([P.any, P.nonNullable.select()], value =>
                    value.format('YYYY-MM-DD'),
                )
                .otherwise(() => null),
        )
    }

    const openTo = match(type)
        .with('day', () => 'day')
        .with('month', () => 'month')
        .otherwise(() => undefined) as DateView | undefined

    return (
        <Controller
            rules={rules}
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
                <LocalizationProvider
                    adapterLocale="pt-br"
                    dateAdapter={AdapterDayjs}
                    localeText={{
                        cancelButtonLabel: 'Cancelar',
                        okButtonLabel: 'Confirmar',
                        datePickerToolbarTitle: 'Selecione uma data',
                        selectViewText(view) {
                            return match(view)
                                .with('hours', () => 'Horas')
                                .with('meridiem', () => 'Período')
                                .with('minutes', () => 'Minutos')
                                .with('seconds', () => 'Segundos')
                                .exhaustive()
                        },
                    }}
                >
                    <DatePicker
                        label={label}
                        views={viewsPicker}
                        openTo={openTo}
                        value={date}
                        onChange={handleChange}
                        disabled={disabled}
                        readOnly={readonly}
                        sx={{
                            display: hidden ? 'none' : 'block',
                        }}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                disabled,
                                error: !!error,
                                required,
                                size,
                                variant,
                                helperText:
                                    error && !unHelperText
                                        ? error.message
                                        : helperText
                                          ? helperText
                                          : unHelperText
                                            ? null
                                            : ' ',
                            },
                        }}
                    />
                    <input type="hidden" {...field} />
                </LocalizationProvider>
            )}
        />
    )
}
